import { estimateFacingsWithDividers } from "@CommonUtils/pog/dividerPolicy";
import { applyTellOProposal } from "./build";
import { tellOGroupLabel } from "./dictionary";
import { tellOEnglish, type TellOTranslate } from "./messages";
import { tellOLevelLabel } from "./products";
import { tellOFacts, type TellOContext, type TellOFacts } from "./facts";
import { displayTellOLength, formatTellOLength } from "./lengths";
import type {
  RuleProposal,
  TellOActionProposal,
  TellOCannotReason,
  TellOChange,
  TellOFeature,
  TellOGroup,
  TellOQuestion,
  TellOReading,
  TellOShelfProposal,
  TellOUnit,
} from "./proposal";

export const TELL_O_COPY = {
  placeholder: "Tell O what to change…",
  send: "Send",
  sendLabel: "Tell O",
  thinking: "O is reading that…",
  offline: "O can't read free text right now. Tap a chip, or open Advanced settings.",
  undo: "Undo",
  undone: "Back to O's default.",
  showDetails: "Show details",
  hideDetails: "Hide details",
  apply: "Apply",
  notThat: "Not that",
  doThat: "Do that",
  neverMind: "Never mind",
} as const;

export interface TellOReplyAction {
  label: string;
  proposal?: RuleProposal;
  primary?: boolean;
}

export interface TellOReply {
  sentence: string;
  preview: string | null;
  rationale: string | null;
  actions: TellOReplyAction[];
}

const productsWord = (count: number, t: TellOTranslate) => t(count === 1 ? "product" : "products");

export const localizedTellOGroupLabel = (
  group: TellOGroup,
  t: TellOTranslate = tellOEnglish,
): string =>
  group.kind === "position"
    ? t(
        group.position === "top"
          ? "groupTop"
          : group.position === "bottom"
            ? "groupBottom"
            : "groupNumber",
        { position: group.position },
      )
    : tellOGroupLabel(group);

const width = (facts: TellOFacts, unit: TellOUnit, t: TellOTranslate) =>
  facts.hardware?.width ? t("wide", { value: formatTellOLength(facts.hardware.width, unit) }) : "";

const depthText = (depths: readonly number[], unit: TellOUnit, t: TellOTranslate) => {
  if (depths.length === 0) return null;
  const low = Math.min(...depths);
  const high = Math.max(...depths);
  return low === high
    ? formatTellOLength(low, unit)
    : t("range", { low: displayTellOLength(low), high: formatTellOLength(high, unit) });
};

const deep = (facts: TellOFacts, unit: TellOUnit, t: TellOTranslate) => {
  const depth = depthText(facts.loaderDepths, unit, t);
  return depth ? t("deep", { value: depth }) : "";
};

const dividerCounts = (facts: TellOFacts, t: TellOTranslate) =>
  t("counts", {
    products: t("productCount", { count: facts.dividerProducts, total: facts.products }),
    shelves: t("shelfCount", { count: facts.dividerShelves, total: facts.shelves }),
  });
const loaderCounts = (facts: TellOFacts, t: TellOTranslate) =>
  t("counts", {
    products: t("productCount", { count: facts.loaderProducts, total: facts.products }),
    shelves: t("shelfCount", { count: facts.loaderShelves, total: facts.loaderShelvesTotal }),
  });
const shortCounts = (
  count: number,
  total: number,
  shelves: number,
  shelfTotal: number,
  t: TellOTranslate,
) =>
  t("shortCounts", {
    count,
    total,
    shelves: t("shelfCount", { count: shelves, total: shelfTotal }),
  });

export const describeTellOPreview = (facts: TellOFacts, t: TellOTranslate = tellOEnglish) =>
  t(facts.loaderMode === "shelf" ? "previewKeep" : "previewGet", {
    dividers: facts.dividerShelves,
    total: facts.shelves,
    loaders: facts.loaderShelves,
  });

const describeProducts = (
  change: Extract<TellOChange, { type: "products" }>,
  facts: TellOFacts,
  unit: TellOUnit,
  t: TellOTranslate,
) => {
  const group = change.group ? localizedTellOGroupLabel(change.group, t) : "";
  const feature = change.features.includes("dividers")
    ? change.features.includes("loaders")
      ? "both"
      : "dividers"
    : "loaders";
  const op = { only: "Only", all: "All", except: "Except", add: "Add" } as const;
  const lead = t(`${feature}${op[change.op]}`, { group });
  if (feature === "both") {
    const products =
      facts.dividerProducts === facts.loaderProducts
        ? t("productCount", { count: facts.dividerProducts, total: facts.products })
        : t("bothProductCounts", {
            dividers: facts.dividerProducts,
            loaders: facts.loaderProducts,
            total: facts.products,
          });
    return t("bothChange", {
      lead,
      products,
      dividers: facts.dividerShelves,
      total: facts.shelves,
      loaders: facts.loaderShelves,
    });
  }
  if (feature === "dividers") {
    const counts =
      change.op === "all"
        ? shortCounts(facts.dividerProducts, facts.products, facts.dividerShelves, facts.shelves, t)
        : dividerCounts(facts, t);
    return t("dividerChange", { lead, counts, width: width(facts, unit, t) });
  }
  return t("loaderChange", { lead, counts: loaderCounts(facts, t), depth: deep(facts, unit, t) });
};

const describeWidth = (
  change: Extract<TellOChange, { type: "width" }>,
  facts: TellOFacts,
  unit: TellOUnit,
  t: TellOTranslate,
) => {
  const instead =
    change.previousWidth !== null && change.previousWidth !== change.width
      ? t("instead", { previous: displayTellOLength(change.previousWidth) })
      : "";
  const head = t("widthChange", {
    width: formatTellOLength(change.width, unit),
    instead,
    counts: shortCounts(
      facts.dividerProducts,
      facts.products,
      facts.dividerShelves,
      facts.shelves,
      t,
    ),
  });
  const shelfWidth = facts.typicalShelfWidth;
  const facingWidth = facts.typicalFacingWidth;
  const hardware = facts.hardware;
  const previousWidth = change.previousWidth;
  if (!hardware || previousWidth === null || !shelfWidth || !facingWidth) return head;
  const fit = (dividerWidth: number) =>
    estimateFacingsWithDividers({
      shelfWidth,
      facingWidth,
      hardware: { ...hardware, width: dividerWidth },
    }).with;
  const difference = fit(previousWidth) - fit(change.width);
  if (difference === 0) return head;
  return t(difference > 0 ? "fewerFacings" : "moreFacings", {
    head,
    difference: Math.abs(difference),
    width: displayTellOLength(shelfWidth),
    unit,
  });
};

const describeRuleChange = (
  change: TellOChange,
  facts: TellOFacts,
  unit: TellOUnit,
  t: TellOTranslate,
): string => {
  switch (change.type) {
    case "products":
      return describeProducts(change, facts, unit, t);
    case "like-base":
      return t("dividerChange", {
        lead: t("likeBase"),
        counts: dividerCounts(facts, t),
        width: width(facts, unit, t),
      });
    case "clear":
      if (change.feature === "dividers")
        return facts.loaderProducts === 0 && facts.loaderShelves === 0
          ? t("clearDividersEmpty")
          : t("clearDividers", { counts: loaderCounts(facts, t), depth: deep(facts, unit, t) });
      return facts.dividerProducts === 0 && facts.dividerShelves === 0
        ? t("clearLoadersEmpty")
        : t("clearLoaders", { counts: dividerCounts(facts, t), width: width(facts, unit, t) });
    case "width":
      return describeWidth(change, facts, unit, t);
    case "depth": {
      const instead =
        change.previousDepth !== null && change.previousDepth !== change.depth
          ? t("instead", { previous: displayTellOLength(change.previousDepth) })
          : "";
      return t("depthChange", {
        depth: formatTellOLength(change.depth, unit),
        instead,
        counts: shortCounts(
          facts.loaderProducts,
          facts.products,
          facts.loaderShelves,
          facts.loaderShelvesTotal,
          t,
        ),
      });
    }
    case "keep":
      return t(change.keep ? "keepOn" : "keepOff");
    case "loader-mode":
      if (change.mode === "products")
        return t("loaderProducts", { counts: loaderCounts(facts, t), depth: deep(facts, unit, t) });
      if (facts.loaderShelves === 0) return t("loaderShelvesEmpty");
      return t("loaderShelves", {
        count: facts.loaderShelves,
        shelves: t(facts.loaderShelves === 1 ? "shelf" : "shelves"),
        depth: deep(facts, unit, t),
      });
  }
};

const describeShelf = (proposal: TellOShelfProposal, unit: TellOUnit, t: TellOTranslate) => {
  const where = t("shelfWhere", {
    bayNo: proposal.bayNo,
    shelfNo: proposal.shelfNo,
    storeCode: proposal.storeCode,
    base: proposal.isBaseStore ? t("baseStore") : "",
  });
  if (proposal.feature === "dividers")
    return t(
      proposal.dividersSetByHand === null
        ? "shelfDividerRule"
        : proposal.dividersSetByHand === "never"
          ? "shelfDividerNever"
          : "shelfDividerAlways",
      { where },
    );
  return t(
    proposal.loaderDepth === null
      ? "shelfLoaderRule"
      : proposal.loaderDepth === 0
        ? "shelfLoaderNone"
        : "shelfLoaderDepth",
    { where, depth: formatTellOLength(proposal.loaderDepth ?? 0, unit) },
  );
};

export const describeTellOShelfSaved = (
  proposal: TellOShelfProposal,
  t: TellOTranslate = tellOEnglish,
) =>
  t("saved", { bayNo: proposal.bayNo, shelfNo: proposal.shelfNo, storeCode: proposal.storeCode });
export const describeTellOShelfUndone = (
  proposal: TellOShelfProposal,
  t: TellOTranslate = tellOEnglish,
) => t("shelfUndone", { bayNo: proposal.bayNo, shelfNo: proposal.shelfNo });

const featureNoun = (feature: TellOFeature, t: TellOTranslate) => t(feature).toLowerCase();
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export const describeTellOCannot = (
  reason: TellOCannotReason,
  unit: TellOUnit,
  t: TellOTranslate = tellOEnglish,
): string => {
  switch (reason.code) {
    case "shelf-position": {
      const one = t(
        reason.position === "top"
          ? "positionTop"
          : reason.position === "bottom"
            ? "positionBottom"
            : "positionNumber",
        { position: reason.position },
      );
      const many = t(
        reason.position === "top"
          ? "positionTopMany"
          : reason.position === "bottom"
            ? "positionBottomMany"
            : "positionNumber",
        { position: reason.position },
      );
      const noun = featureNoun(reason.feature, t);
      const head = t("cannotPosition", { feature: capitalize(noun), position: one });
      return reason.products > 0
        ? t("nearestPosition", {
            head,
            feature: noun,
            count: reason.products,
            products: productsWord(reason.products, t),
            sit: t(reason.products === 1 ? "sits" : "sit"),
            position: many,
          })
        : head;
    }
    case "every-store-override":
      return reason.storeCode
        ? t("nearestStore", {
            bayNo: reason.bayNo,
            shelfNo: reason.shelfNo,
            storeCode: reason.storeCode,
          })
        : t("oneStore");
    case "unknown-products":
      return t("unknownProducts", { phrase: reason.phrase });
    case "not-understood":
      return t("notUnderstood");
    case "both-modes":
      return t("bothModes");
    case "shelf-not-found":
      return t("shelfNotFound", {
        bayNo: reason.bayNo,
        shelfNo: reason.shelfNo,
        bays: reason.bays,
        bayWord: t(reason.bays === 1 ? "bay" : "bays"),
        shelves: reason.shelves,
        shelfWord: t(reason.shelves === 1 ? "shelf" : "shelves"),
      });
    case "depth-too-deep":
      return t("depthTooDeep", {
        depth: formatTellOLength(reason.depth, unit),
        shallowest: formatTellOLength(reason.shallowest, unit),
        deepest: formatTellOLength(reason.deepest, unit),
      });
  }
};

export const describeTellOReading = (
  { phrase, group }: TellOReading,
  t: TellOTranslate = tellOEnglish,
): string | null => {
  const count = group.productCodes.length;
  switch (group.kind) {
    case "cdt":
    case "attribute": {
      const detail =
        group.kind === "attribute"
          ? group.attributeLabel
          : group.levelLabel === tellOLevelLabel(group.level)
            ? t("level", { level: Number(group.level.slice(3)) + 1 })
            : group.levelLabel;
      return t("readingGroup", {
        phrase,
        value: group.value,
        detail,
        count,
        products: productsWord(count, t),
      });
    }
    case "name":
      return t("readingName", {
        phrase,
        count,
        products: productsWord(count, t),
        word: group.word,
      });
    case "product":
      return t("readingProduct", {
        phrase,
        product: group.name ? `${group.name} (${group.productCode})` : group.productCode,
      });
    case "position":
      return null;
  }
};

export const describeTellOQuestion = (
  question: TellOQuestion,
  t: TellOTranslate = tellOEnglish,
): string => {
  switch (question.type) {
    case "feature": {
      const label = localizedTellOGroupLabel(question.group, t);
      const count = question.group.productCodes.length;
      const phrase = question.phrase.trim().replace(/[.?!]+$/, "");
      return t(
        phrase && phrase.toLowerCase() !== label.toLowerCase()
          ? "questionFeaturePhrase"
          : "questionFeature",
        { phrase: capitalize(phrase), label, count, products: productsWord(count, t) },
      );
    }
    case "which":
      return t("questionWhich", { phrase: question.phrase });
    case "in-or-out":
      return t("questionInOut", {
        phrase: question.phrase,
        feature: featureNoun(question.feature, t),
      });
    case "shelf-feature":
      return t("questionShelf", { bayNo: question.bayNo, shelfNo: question.shelfNo });
  }
};

export const describeTellOGroupOption = (group: TellOGroup, t: TellOTranslate = tellOEnglish) =>
  t("groupOption", { label: localizedTellOGroupLabel(group, t), count: group.productCodes.length });

const previewOf = (context: TellOContext, proposal: TellOActionProposal, t: TellOTranslate) => {
  if (proposal.kind === "undo") return null;
  if (proposal.kind === "rule")
    return describeTellOPreview(
      tellOFacts(context, applyTellOProposal(context.policy, proposal)),
      t,
    );
  const override = proposal.isBaseStore
    ? proposal.feature === "dividers"
      ? {
          bayNo: proposal.bayNo,
          shelfNo: proposal.shelfNo,
          dividersSetByHand: proposal.dividersSetByHand,
        }
      : { bayNo: proposal.bayNo, shelfNo: proposal.shelfNo, loaderDepth: proposal.loaderDepth }
    : null;
  return describeTellOPreview(tellOFacts(context, context.policy, override), t);
};

const questionOption = (question: TellOQuestion, index: number, t: TellOTranslate) => {
  switch (question.type) {
    case "feature":
      return t(index === 0 ? "dividers" : index === 1 ? "loaders" : "both");
    case "shelf-feature":
      return t(index === 0 ? "dividers" : "loaders");
    case "in-or-out":
      return t(index === 0 ? "give" : "takeAway");
    case "which": {
      const group = question.groups[index];
      return group ? describeTellOGroupOption(group, t) : t("neither");
    }
  }
};

export const describeTellOProposal = (
  context: TellOContext,
  proposal: RuleProposal,
  t: TellOTranslate = tellOEnglish,
): TellOReply => {
  const unit = context.unit;
  switch (proposal.kind) {
    case "undo":
      return { sentence: t("undone"), preview: null, rationale: null, actions: [] };
    case "rule": {
      const after = applyTellOProposal(context.policy, proposal);
      return {
        sentence: describeRuleChange(proposal.change, tellOFacts(context, after), unit, t),
        preview: previewOf(context, proposal, t),
        rationale: proposal.reading ? describeTellOReading(proposal.reading, t) : null,
        actions: [{ label: t("apply"), proposal, primary: true }, { label: t("notThat") }],
      };
    }
    case "shelf":
      return {
        sentence: describeShelf(proposal, unit, t),
        preview: previewOf(context, proposal, t),
        rationale: null,
        actions: [{ label: t("apply"), proposal, primary: true }, { label: t("notThat") }],
      };
    case "question":
      return {
        sentence: t === tellOEnglish ? proposal.text : describeTellOQuestion(proposal.question, t),
        preview: null,
        rationale: null,
        actions: proposal.options.map((option, index) => ({
          label: t === tellOEnglish ? option.label : questionOption(proposal.question, index, t),
          proposal: option.proposal,
        })),
      };
    case "cannot":
      return {
        sentence: describeTellOCannot(proposal.reason, unit, t),
        preview: null,
        rationale: null,
        actions: [
          ...(proposal.nearest
            ? [
                {
                  label:
                    proposal.reason.code === "depth-too-deep"
                      ? t("useDepth", { depth: formatTellOLength(proposal.reason.deepest, unit) })
                      : t("doThat"),
                  proposal: proposal.nearest,
                },
              ]
            : []),
          { label: t("neverMind") },
        ],
      };
  }
};
